import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CreditCard } from 'lucide-react';
import { getOrganizationBilling } from '../../../services/organizationService';
import { createCheckoutSession, createSetupSession, getPaymentMethod, deletePaymentMethod } from '../../../services/checkoutService';

const Billing = ({ organizationId }) => {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [setupSessionLoading, setSetupSessionLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState(false);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        const response = await getOrganizationBilling(organizationId);
        console.log('Billing data response:', response);
        setBillingData(response.data.data);
      } catch (err) {
        console.error('Error fetching billing data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchBillingData();
      fetchPaymentMethod();
    }
  }, [organizationId]);

  const fetchPaymentMethod = async () => {
    try {
      setPaymentMethodLoading(true);
      const response = await getPaymentMethod(organizationId);
      console.log('Payment method response:', response);
      setPaymentMethod(response.data);
    } catch (err) {
      console.error('Error fetching payment method:', err);
    } finally {
      setPaymentMethodLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-600">Error loading billing data: {error}</p>
      </div>
    );
  }

  const { credits, subscription, pricingModel, recentRuns } = billingData || {};
  const currentMonthStats = credits?.monthlyStats?.currentMonth || {};
  const previousMonthStats = credits?.monthlyStats?.previousMonth || {};

  // Handle checkout session creation
  const handleAddCredits = async () => {
    if (!organizationId) {
      setError('Organization ID is missing. Please refresh the page.');
      return;
    }

    try {
      setCheckoutLoading(true);
      const response = await createCheckoutSession(organizationId);
      
      if (response.data?.url) {
        window.location.href = response.data.url;
        // Keep loading state active until redirect completes
        return;
      } else {
        setError('Failed to create checkout session');
        setCheckoutLoading(false);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.message || 'Unable to create checkout session. Please try again.');
      setCheckoutLoading(false);
    }
  };

  // Handle setup payment method
  const handleSetupPaymentMethod = async () => {
    if (!organizationId) {
      setError('Organization ID is missing. Please refresh the page.');
      return;
    }

    try {
      setSetupSessionLoading(true);
      const response = await createSetupSession(organizationId);
      
      if (response.data?.url) {
        window.location.href = response.data.url;
        // Keep loading state active until redirect completes
        return;
      } else {
        setError('Failed to create setup session');
        setSetupSessionLoading(false);
      }
    } catch (err) {
      console.error('Error creating setup session:', err);
      setError(err.response?.data?.message || 'Unable to setup payment method. Please try again.');
      setSetupSessionLoading(false);
    }
  };

  // Handle delete payment method
  const handleDeletePaymentMethod = async () => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      setDeletingPaymentMethod(true);
      await deletePaymentMethod(organizationId);
      // Refresh payment method
      fetchPaymentMethod();
    } catch (err) {
      console.error('Error deleting payment method:', err);
      setError(err.response?.data?.message || 'Unable to delete payment method. Please try again.');
    } finally {
      setDeletingPaymentMethod(false);
    }
  };

  // Prepare recent runs data for chart - always show 10 bars
  const chartData = Array.from({ length: 10 }, (_, idx) => {
    const runs = (recentRuns || []).slice(0, 10).reverse(); // Get first 10 and reverse to show chronological order
    const run = runs[idx];
    return {
      id: idx + 1,
      cost: run ? run.cost?.total || 0 : 0, // Keep full precision for accurate scaling
      tokens: run ? run.tokens?.total || 0 : 0,
      date: run ? new Date(run.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'
    };
  });

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Pay as You Go Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Pay as you go</h2>
        
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Credit balance</p>
          <p className="text-4xl font-bold text-gray-900">${credits?.available?.toFixed(2)}</p>
        </div>

        <div className="flex gap-3 mb-8">

          <button 
            onClick={() => window.location.href = 'mailto:hello@ga4.so'}
            className="bg-brand-pink hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium py-2 px-5 rounded transition-opacity"
          >
            Request Credits
          </button> 

          {/* 
            ------ >>>>>>>>  REACTIVATE  this code block later
           <button 
            onClick={handleAddCredits}
            disabled={checkoutLoading}
            className="bg-brand-pink hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium py-2 px-5 rounded transition-opacity"
          >
            {checkoutLoading ? 'Loading...' : 'Add to credit balance'}
          </button> 
          */}
          {/* 
           ------ >>>>>>>>  REACTIVATE  this code block later 
          {!paymentMethod?.hasPaymentMethod && (
            <button 
              onClick={handleSetupPaymentMethod}
              disabled={setupSessionLoading}
              className="bg-gray-900 hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium py-2 px-5 rounded transition-colors"
            >
              {setupSessionLoading ? 'Loading...' : 'Enable auto charging'}
            </button>
          )} */}

        </div>

        {/* Payment Method Section */}
        {!paymentMethodLoading && paymentMethod?.hasPaymentMethod && (
          <div className="border border-green-200 bg-green-50 rounded-lg p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="text-green-600 flex-shrink-0 mt-0.5">
                  <CreditCard className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-900 mb-1">Auto recharge is on</h4>
                  <p className="text-xs text-green-800 leading-relaxed mb-2">
                    When your credit balance reaches ${paymentMethod?.autoRecharge?.threshold}.00, your payment method will be charged to bring the balance up to ${paymentMethod?.autoRecharge?.rechargeAmount}.00. The maximum amount of credits that will automatically recharge in a month is ${paymentMethod?.autoRecharge?.rechargeAmount}.00.
                  </p>
                  <p className="text-xs text-gray-600 italic">
                    {paymentMethod?.paymentMethod?.type} ending in {paymentMethod?.paymentMethod?.last4} • Expires {paymentMethod?.paymentMethod?.expMonth}/{paymentMethod?.paymentMethod?.expYear}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button 
                  onClick={handleSetupPaymentMethod}
                  disabled={setupSessionLoading}
                  className="bg-gray-900 hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-semibold py-1.5 px-3 rounded transition-colors"
                >
                  {setupSessionLoading ? 'Loading...' : 'Modify'}
                </button>
                <button
                  onClick={handleDeletePaymentMethod}
                  disabled={deletingPaymentMethod}
                  className="bg-red-100 hover:bg-red-200 disabled:opacity-60 disabled:cursor-not-allowed text-red-600 text-xs font-semibold py-1.5 px-3 rounded transition-colors"
                >
                  {deletingPaymentMethod ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Usage Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Recent Usage — Last {chartData.length} Runs</h3>
            <p className="text-xs text-gray-500 mt-1">Cost and token consumption per execution</p>
          </div>
          
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 40, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis 
                dataKey="id" 
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)',
                  fontSize: '12px'
                }}
                cursor={{ fill: 'rgba(139, 92, 246, 0.08)' }}
                content={({ active, payload, label }) => {
                  if (active && payload?.length > 0) {
                    const data = payload[0].payload;
                    return (
                      <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)'
                      }}>
                        <p style={{ color: '#6b7280', fontSize: '11px', margin: '0 0 6px 0' }}>{data.date}</p>
                        <p style={{ color: '#1f2937', fontSize: '12px', fontWeight: '500', margin: '4px 0' }}>Cost: ${data.cost.toFixed(3)}</p>
                        <p style={{ color: '#1f2937', fontSize: '12px', fontWeight: '500', margin: '4px 0' }}>Tokens: {data.tokens.toLocaleString()}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar 
                dataKey="cost" 
                fill="#8b5cf6" 
                radius={[4, 4, 0, 0]} 
                isAnimationActive={true}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Activity Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">This Month</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Runs Executed</span>
              <span className="text-lg font-semibold text-gray-900">{currentMonthStats.runsExecuted}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Total Spent</span>
              <span className="text-lg font-semibold text-gray-900">${currentMonthStats.totalCost?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Avg per Run</span>
              <span className="text-lg font-semibold text-gray-900">
                ${(currentMonthStats.totalCost / Math.max(currentMonthStats.runsExecuted, 1)).toFixed(3)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Last Month</h3>
          {previousMonthStats.runsExecuted > 0 ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Runs Executed</span>
                <span className="text-lg font-semibold text-gray-900">{previousMonthStats.runsExecuted}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Total Spent</span>
                <span className="text-lg font-semibold text-gray-900">${previousMonthStats.totalCost?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Change</span>
                <span className={`text-lg font-semibold ${
                  currentMonthStats.totalCost > previousMonthStats.totalCost ? 'text-red-600' : 'text-green-600'
                }`}>
                  {currentMonthStats.totalCost > previousMonthStats.totalCost ? '↑' : '↓'}
                  {Math.abs(((currentMonthStats.totalCost - previousMonthStats.totalCost) / Math.max(previousMonthStats.totalCost, 1) * 100)).toFixed(0)}%
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No activity</p>
          )}
        </div>
      </div>

      
    </div>
  );
};

export default Billing;
